import { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import { MEMBERSHIP_OPTIONS } from "components/signup/types";
import { MembershipOptionCard } from "components/signup/components/MembershipOptionCard";
import { PlanFeatures } from "components/signup/components/PlanFeatures";
import FlexBox from "components/common/FlexBox";
import { FAQ } from "components/signup/components/FAQ";
import { useUpdateSessionForMember } from "services/signup";
import { buildSessionUpdate } from "components/signup/utils";

export default ({
  useContinueText = false,
  hideTitle = false,
}: {
  useContinueText?: boolean;
  hideTitle?: boolean;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<number | null>(1); // Free Time Finder
  const { request: updateSession, loading } = useUpdateSessionForMember();

  const handleContinue = async () => {
    if (selectedPlan === null) return;
    const membership = MEMBERSHIP_OPTIONS[selectedPlan];

    const updates = buildSessionUpdate({ membership });
    await updateSession(updates);

    navigate("/upgrade/upgrade-payment", {
      state: { membership },
    });
  };

  return (
    <FlexBox
      variant={isMobile ? "default" : "contained"}
      direction="column"
      sx={{
        padding: isMobile ? "0" : "32px",
        gap: "24px",
      }}
    >
      {/* Header Section */}
      {!hideTitle && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography variant="h2" sx={{ width: "100%", textAlign: "center" }}>
            Choose your membership
          </Typography>
        </Box>
      )}

      {/* Content Section */}
      <Box
        sx={{
          display: "flex",
          gap: isMobile ? "24px" : "48px",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
        }}
      >
        {isMobile && (
          <FlexBox direction="column">
            <Typography variant="bodyHeavy">All plans include</Typography>
            <PlanFeatures />
            <FAQ
              question="How to choose the best plan for you"
              answer="Most families with kids or multiple account holders start with the 8-hour plan, which works well for a fuller to-do list. If your needs are lighter, the 4-hour plan is a great place to start. All plans are month to month, so you can adjust as life changes."
            />
          </FlexBox>
        )}

        {/* Left side - Membership Options */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            gap: "12px",
            width: "100%",
          }}
        >
          {MEMBERSHIP_OPTIONS.filter((m) => !m.hidden).map((option, idx) => (
            <Box
              onClick={() => setSelectedPlan(idx)}
              key={option.name}
              sx={{ cursor: "pointer" }}
            >
              <MembershipOptionCard
                option={option}
                isSelected={selectedPlan === idx}
              />
            </Box>
          ))}
          {/* <Button variant="text" onClick={() => navigate("/consultation")}>
            Not sure? Schedule a consultation
          </Button> */}
        </Box>

        {/* Right side - Plan Features */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          {!isMobile && (
            <FlexBox direction="column" flex={0}>
              <Typography variant="bodyHeavy">All plans include</Typography>
              <PlanFeatures />
              <FAQ
                question="How to choose the best plan for you"
                answer="Most families with kids or multiple account holders start with the 8-hour plan, which works well for a fuller to-do list. If your needs are lighter, the 4-hour plan is a great place to start. All plans are month to month, so you can adjust as life changes."
              />
            </FlexBox>
          )}

          {/* CTA Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-end",
              flexDirection: isMobile ? "column" : "row",
              gap: "12px",
              alignItems: isMobile ? "center" : "end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              disabled={selectedPlan === null || loading}
              sx={{
                width: isMobile ? "100%" : "fit-content",
                bgcolor: "#198282",
                "&:hover": {
                  bgcolor: "#276767",
                },
                "&.Mui-disabled": { backgroundColor: "#EAEBEC" },
                borderRadius: "32px",
                textTransform: "none",
              }}
            >
              {isMobile || useContinueText
                ? "Continue"
                : selectedPlan !== null
                  ? MEMBERSHIP_OPTIONS[selectedPlan].name
                  : "Plan"}
            </Button>
          </Box>
        </Box>
      </Box>
    </FlexBox>
  );
};
